import { useMemo } from "react";
import { css } from "@emotion/react";
import { FormControl, useTheme } from "@mui/material";
import {
  DesktopDateTimePicker,
  MobileDateTimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useTranslation } from "react-i18next";
import { Element } from "react-scroll";

import { dayjs } from "@thalamos/common";
import type { Dayjs } from "@thalamos/common";
import type { DateTimeFormBuilderField } from "@thalamos/aspire-common";

import type { FieldProps } from "../../pages/FieldProps.js";
import { FormLabel } from "../design-system/FormLabel.js";
import { HelperText } from "../design-system/HelperText/HelperText.js";
import { ukInstantOfWallClock } from "./ukInstantOfWallClock.js";
import { wallClockOfUkInstant } from "./wallClockOfUkInstant.js";
import { useScreenDetection } from "~/hooks/ScreenDetection/useScreenDetection.js";

export type DateTimeFormFieldProps<Data extends { [k: string]: any }> = {
  field: DateTimeFormBuilderField<Data>;
  fieldProps: FieldProps<Data>;
};

export function DateTimeFormField<Data extends { [k: string]: any }>({
  field,
  fieldProps,
}: DateTimeFormFieldProps<Data>) {
  const isError = !!(
    fieldProps.touched[field.field] && fieldProps.errors[field.field]
  );
  const { t } = useTranslation();

  const theme = useTheme();

  const { isMobileView } = useScreenDetection();

  const DateTimePicker = isMobileView
    ? MobileDateTimePicker
    : DesktopDateTimePicker;

  const fieldValue: unknown = fieldProps.values[field.field];
  const shownValue =
    typeof fieldValue === "string" && dayjs(fieldValue).isValid()
      ? wallClockOfUkInstant(dayjs(fieldValue))
      : null;

  const pickerTextFieldSx = useMemo(
    () => ({
      "& .MuiPickersOutlinedInput-root": {
        backgroundColor: theme.palette.common.paleGray,
      },
    }),
    [theme],
  );

  return (
    <Element
      name={field.field}
      data-testid={`field:${field.field}`}
      css={css`
        margin-bottom: ${theme.spacing(3)};
      `}
    >
      {field.label && <FormLabel label={field.label} error={isError} />}

      <FormControl>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePicker
            ampm={false}
            format={"DD/MM/YYYY HH:mm"}
            minDateTime={
              field.minimum && wallClockOfUkInstant(field.minimum(dayjs()))
            }
            maxDateTime={
              field.maximum && wallClockOfUkInstant(field.maximum(dayjs()))
            }
            onChange={(value: Dayjs | null) => {
              fieldProps.setFieldTouched(field.field, true, false);
              fieldProps.setValues({
                ...fieldProps.values,
                // A half-typed value is kept as text, so validation reports it
                // as an invalid date rather than as a missing one.
                [field.field]: value?.isValid()
                  ? ukInstantOfWallClock(value).toISOString()
                  : (value?.toString() ?? null),
              });
            }}
            value={shownValue}
            slotProps={{
              textField: {
                error: isError,
                fullWidth: true,
                sx: pickerTextFieldSx,
              },
            }}
          />
        </LocalizationProvider>
      </FormControl>
      {!isError && field.showDateTimeMessage !== false && (
        <HelperText subtext={t("forms.ukDateTimeWarning")} />
      )}
      {isError && (
        <HelperText errorMessage={fieldProps.errors[field.field] as string} />
      )}
    </Element>
  );
}
